import { TranslationMessages } from 'react-admin';
import englishMessages from 'ra-language-english';

// The react-admin English messages are already complete,
// we're just overriding a few specific ones to customize them
const customEnglishMessages = {
  ...englishMessages,
  ra: {
    ...englishMessages.ra,
    auth: {
      ...englishMessages.ra.auth,
      email: 'Email',
      password: 'Password',
      sign_in: 'Sign in',
      remember_me: 'Remember me',
      sign_in_error: 'Authentication failed, please try again',
      logout: 'Logout',
    },
    notification: {
      ...englishMessages.ra.notification,
      updated: 'Item updated successfully',
      created: 'Item created successfully',
      deleted: 'Item deleted successfully',
    },
    page: {
      ...englishMessages.ra.page,
      empty: 'No %{name} found',
      invite: 'Create the first record to get this workspace set up.',
    },
  },
  // Layout and navigation
  layouts: {
    layout: {
      footer: {
        privacy: 'Privacy',
        conditions: 'Conditions',
      },
      header: {
        settings: 'Settings',
        signout: 'Sign out',
        manage_account: 'Manage account',
        reports: 'Reports',
        your_profile: 'Your profile',
        dashboard: 'Dashboard',
        home: 'Home',
        join: 'Join',
        signin: 'Sign in',
      },
    },
  },
  // Common form elements
  common: {
    form: {
      no: 'No',
      yes: 'Yes',
      not_applicable: 'Not applicable',
      save: 'Save',
      active: 'Active',
      inactive: 'Inactive',
      select_year: 'Select Year',
      select_month: 'Select Month',
      select_day: 'Select Day',
      next: 'Next',
      close: 'Close',
      cancel: 'Cancel',
      complete: 'Complete',
      loading: 'Loading...',
      size: 'Size',
      start: 'Start',
      get_started: 'Get Started',
    },
    nav: {
      back: 'Back',
    },
    upload: {
      drag_drop_or_click: 'Drag & drop or click to upload',
      choose_file: 'Choose a file or drag it here',
      browse_files: 'Browse Files',
      drop_file_here: 'Drop file here to upload',
      release_to_upload: 'Release to upload your file',
      uploading: 'Uploading {fileName}...',
      percent_complete: '% Complete',
      file_size_mb: '{size} MB',
      upload_file: 'Upload File',
      upload_files: 'Upload {count} Files',
      max_file_size: 'Max file size: {maxSizeMB}MB',
      accepted_formats: 'Accepted formats:',
      multiple_files_allowed: 'Multiple files allowed',
      file_upload_required: 'File upload is required',
      files_uploaded_success: '{count} file(s) uploaded successfully',
      upload_failed: 'Upload failed: ',
      file_size_exceeds: 'File "{fileName}" exceeds {maxSizeMB}MB limit',
    },
  },
  // Wizard and forms
  wizard: {
    progress: {
      step_of: 'Step {current} of {total}',
      question_of: 'Question {current} of {total}',
      percent_complete: '% complete',
      profile_builder: 'Profile Builder',
    },
    welcome: {
      title: 'Welcome aboard to Driver Verified',
      subtitle: "Let's set up your Driver Profile",
      time_estimate: '⏱ 15 mins',
      setup_profile_message:
        'Setup your profile to unlock the DriverVerified Report Center and Resume Export',
    },
    navigation: {
      save_and_continue: 'Save & Continue',
      save_and_exit: 'Save & Exit',
    },
    notifications: {
      driver_profile_saved: 'Driver profile saved successfully',
      license_saved: 'License saved successfully',
      education_saved: 'Education saved successfully',
      employment_saved: 'Employment saved successfully',
      certification_saved: 'Certification saved successfully',
      document_uploaded: 'Document uploaded successfully',
      error_saving_data: 'Error saving data: ',
    },
    empty_state: {
      title: 'No Drivers Yet',
      subtitle:
        'Start building your driver database by creating comprehensive driver profiles. Track licenses, certifications, employment history, and more.',
      action: 'Create Driver Profile',
    },
    steps: {
      basic_info: {
        title: 'Driver Profile Setup',
        select_user: 'Select User',
        experience_radius: 'Experience Radius',
        select_experience_level: 'Select Experience Level',
        loading_users: 'Loading users...',
      },
      license: {
        title: 'Add Licence',
        main_heading: 'Add an active Drivers License',
        license_origin: 'Licence Origin',
        expiry_date: 'Expiry Date',
        license_number: 'License Number',
        select_province: 'Select province...',
        select_class: 'Select License Class',
        enter_license_number: 'Enter license number',
        upload_question: 'Do you want to upload a scan or image of your licence?',
        upload_document: 'Upload License Document',
        select_answer: 'Select answer...',
      },
      education: {
        title: 'Add Driving Education',
        main_heading: 'Driving Education',
        description:
          'At least one education program you have completed is required. If you do not have any education to add, at least one employment record is required.',
        driving_school: 'Driving School',
        school_name: 'School Name',
        program_title: 'Program Title',
        completed_date: 'Completed Date',
        select_school: 'Select School',
        enter_program_title: 'Enter program title',
        upload_question: 'Do you have a education/training document (file)?',
        upload_document: 'Upload Education Document',
        schools: {
          abc_driving: 'ABC Driving School',
          city_academy: 'City Driving Academy',
          professional_institute: 'Professional Drivers Institute',
          highway_safety: 'Highway Safety School',
          other: 'Other',
        },
      },
      certification: {
        title: 'Add Certification',
        description: 'This is where you add any certifications you hold, like CPR or Hazmat.',
        certification_title: '* Certification title',
        select_certification_type: 'Select Certification Type',
        custom_certification_title: 'Custom Certification Title',
        issuing_organization: 'Issuing Organization',
        enter_organization: 'Enter issuing organization',
        date_issued: '* Date Issued',
        expiry_date_optional: 'Expiry Date (Optional)',
        save_and_continue: 'Save & Continue',
        types: {
          cpr: 'CPR Certification',
          hazmat: 'Hazmat Certification',
          first_aid: 'First Aid Certification',
          safety_training: 'Safety Training Certificate',
          other: 'Other',
        },
      },
      driving_experience: {
        title: 'Driving Experience',
        main_heading: 'Radius of Experience',
        description:
          'At least one employment record is required. If you do not have any experience to add, at least one education program is required.',
        company: '* Company',
        country: '* Country',
        enter_company: 'Enter company name',
        select_country: 'Select Country',
        countries: {
          canada: 'Canada',
          usa: 'United States',
        },
        sections: {
          canada_experience: 'Canada Experience',
          usa_experience: 'USA Experience',
          international_experience: 'International Experience',
          accident_history: 'Accident History',
          ticket_history: 'Ticket History',
          citizenship: 'Citizenship',
          drug_alcohol_testing: 'Drug and Alcohol Testing',
          passport: 'Passport',
          fastpass: 'FastPass',
          us_dot: 'U.S. D.O.T.',
          us_entry: 'U.S. Entry',
          vehicle_types: 'Vehicle Types',
          employment_history: 'Employment History',
        },
      },
      overview: {
        sections: {
          drivers_license: 'Drivers License',
          education_employment: 'Education and Employment',
          driving_experience: 'Driving Experience',
          certifications: 'Certifications',
          accountability_files: 'Accountability Files',
        },
      },
    },
  },
  // Main application
  app: {
    login: {
      email: 'Email',
      password: 'Password',
      sign_in: 'Sign In',
      invalid_credentials: 'Invalid email or password',
      dv_logo_alt: 'DV Logo',
    },
    dashboard: {
      title: 'DV Admin Dashboard',
      welcome_title: 'Welcome to DV Admin Portal',
      welcome_description:
        'Manage your users, equipment, and forms from this central admin dashboard.',
      cards: {
        users: 'Users',
        equipment: 'Vehicles',
        forms: 'Forms',
      },
    },
    language: {
      english: 'English',
      french: 'Français',
    },
  },
  // Geographic data
  geography: {
    provinces: {
      AB: 'Alberta',
      BC: 'British Columbia',
      MB: 'Manitoba',
      NB: 'New Brunswick',
      NL: 'Newfoundland and Labrador',
      NS: 'Nova Scotia',
      ON: 'Ontario',
      PE: 'Prince Edward Island',
      QC: 'Quebec',
      SK: 'Saskatchewan',
      NT: 'Northwest Territories',
      NU: 'Nunavut',
      YT: 'Yukon',
    },
    license_classes: {
      class_1: 'Class 1',
      class_2: 'Class 2',
      class_3: 'Class 3',
      class_4: 'Class 4',
      class_5: 'Class 5',
    },
    experience_radius: {
      local: 'Local',
      regional: 'Regional',
      long_haul: 'Long Haul',
    },
  },
  // Empty states for different resources
  empty_states: {
    users: {
      title: 'No Users Yet',
      subtitle:
        'Add users to your system to manage access and permissions. Users can be drivers, administrators, or other team members.',
      action: 'Add User',
    },
    equipment: {
      title: 'No Equipment Yet',
      subtitle:
        'Track your fleet equipment, vehicles, and assets. Manage maintenance schedules and equipment assignments.',
      action: 'Add Equipment',
    },
    forms: {
      title: 'No Forms Yet',
      subtitle:
        'Create custom forms for data collection, inspections, and documentation. Build your workflow with digital forms.',
      action: 'Create Form',
    },
    driver_licenses: {
      title: 'No Driver Licenses',
      subtitle:
        'Driver licenses will appear here when drivers complete their license information through their profiles.',
    },
    driver_certifications: {
      title: 'No Driver Certifications',
      subtitle:
        'Driver certifications will appear here when drivers add their professional certifications through their profiles.',
    },
    driver_education: {
      title: 'No Driver Education Records',
      subtitle:
        'Education and training records will appear here when drivers complete their education information.',
    },
    driver_employment: {
      title: 'No Employment History',
      subtitle:
        'Driver employment records will appear here when drivers add their work experience through their profiles.',
    },
    driver_vehicles: {
      title: 'No Vehicle Experience',
      subtitle:
        'Driver vehicle experience will appear here when drivers complete their vehicle operation history.',
    },
    completed_forms: {
      title: 'No Completed Forms',
      subtitle:
        'Form submissions and responses will appear here once users start completing forms.',
    },
  },
  // Document types
  documents: {
    types: {
      license: 'license document',
      education: 'education/training document',
      certification: 'certification document',
      insurance: 'insurance document',
      registration: 'registration document',
      document: 'document',
    },
    formats: {
      images_pdf_word: 'Images, PDF, Word documents',
      images_only: 'Images only',
      pdf_only: 'PDF documents only',
      all_types: 'All file types',
    },
  },
  // Driver profile details
  user: {
    driver: {
      details: {
        operating_details: 'Operating Details',
        radius: {
          label: 'Experience Radius',
          details: 'What is the longest operating distance of vehicle you have experience with?',
        },
        canada_exp: {
          label: 'Canadian Experience',
          details: 'Have you operated a vehicle in Canada?',
        },
        canada_exp_provinces: {
          label: 'Provincial Experience',
          details: 'Check all provinces you have experience with in Canada.',
        },
        usa_exp: {
          label: 'American Experience',
          details: 'Have you operated a vehicle in the United States of America?',
        },
        usa_exp_states: {
          label: 'State Experience',
          details: 'Check all states you have experience with in the United States of America.',
        },
        international_exp: {
          label: 'International Experience (outside North America)',
          details:
            'Please describe any vehicle operating experience you have outside of Canada and the United States.',
        },
        accident: {
          label: 'Accident Check',
          details: 'Have you been involved in an accident in the past three years?',
        },
        accident_history: {
          label: 'Accident History',
          details: '(If yes) Describe each accident you were involved in in the past three years',
        },
        recent_ticket: {
          label: 'Ticket Check',
          details: 'Have you received any tickets in the past three years?',
        },
        recent_ticket_history: {
          label: 'Ticket History',
          details: '(If yes) Describe each ticket you received in in the past three years',
        },
        can_citizen: {
          label: 'Citizenship',
          details: 'Are you a Canadian citizen?',
        },
        can_employment_eligible: {
          label: 'Employment Eligibility',
          details: '[If No] Do you have a legal right to live and work in Canada?',
        },
        drug_alcohol_testing: {
          label: 'Drug/Alcohol Testing',
          details:
            'Have you ever tested positive or refused to test on any pre-employment Drug or Alcohol test administered by an employer to which you applied for, but did not obtain employment during the past three years?',
        },
        passport_check: {
          label: 'Passport Check',
          details: 'Do you have a valid passport?',
        },
        passport_origin: {
          label: 'Passport Origin',
          details: '[If Yes] Which country is your passport issued by?',
        },
        passport_expiry: {
          label: 'Passport Expiry',
          details: '[If Yes] When does your passport expire?',
        },
        fastpass_check: {
          label: 'FastPass Check',
          details: 'Do you have a FastPass number?',
        },
        fastpass_number: {
          label: 'FastPass Number',
          details: '[If Yes] Enter your FastPass number',
        },
        usdot_check: {
          label: 'U.S. D.O.T. Check',
          details: 'Do you have a U.S. D.O.T. Number?',
        },
        usdot_number: {
          label: 'U.S. D.O.T. Number',
          details: '[If Yes] Enter your U.S. D.O.T. Number',
        },
        us_entry_check: {
          label: 'U.S. Entry Check',
          details: 'Have you ever been denied entry into the US?',
        },
        us_entry_status: {
          label: 'U.S. Entry Status',
          details: '[If Yes] Are you now able to enter the US?',
        },
      },
      vehicletypes_add: {
        select_type: 'Select Vehicle Type...',
        utilization: {
          select: {
            label: 'Select Job Type...',
          },
        },
      },
      vehicletypes: {
        length: '{0} months',
      },
      vehicletypes_view: {
        vehicletype_label: 'Vehicle Type',
        length_months: {
          label: 'Experience (Months)',
        },
        utilization: {
          label: 'Job Type(s)',
        },
        notes: {
          label: 'Notes',
        },
        vehicletype: {
          label: 'Vehicle Type',
        },
      },
      employment_history: {
        country: {
          label: 'Country',
        },
        edit: {
          company: {
            label: 'Company',
          },
          country: {
            label: 'Country',
          },
          description: {
            label: 'Position Description',
            placeholder: 'Enter a position description',
          },
          address1: {
            label: 'Address Line 1',
            placeholder: 'Enter address line 1',
          },
          address2: {
            label: 'Address Line 2',
            placeholder: 'Enter address line 2',
          },
          postal_code: {
            label: 'Postal Code',
            placeholder: 'Enter postal code',
          },
          contact: {
            label: 'Primary Contact',
          },
          contact_name: {
            label: 'Full Name',
            placeholder: 'Enter full name',
          },
          contact_title: {
            label: 'Title',
            placeholder: 'Enter title',
          },
          contact_email: {
            label: 'E-mail Address',
            placeholder: 'Enter e-mail address',
          },
          contact_phone: {
            label: 'Phone Number',
            placeholder: 'Enter phone number',
          },
          contact_phone_ext: {
            label: 'Phone Number Extension',
            placeholder: 'Enter phone extension',
          },
        },
        form: {
          save: 'Save Experience',
          add: 'Add Experience',
        },
        view: {
          title: {
            label: 'Position',
          },
          manage_vehicles: 'Vehicles',
        },
        vehicletypes_edit: {
          vehicle_year: {
            label: 'Vehicle Year',
          },
          vehicle_make: {
            label: 'Vehicle Make',
          },
          vehicle_model: {
            label: 'Vehicle Model',
          },
        },
        vehicletypes_view: {
          vehicle_year: {
            label: 'Vehicle Year',
          },
          label: 'Images',
          images: {
            label: 'Images',
            add: 'Add Image',
          },
        },
        job_policy: {
          loe_broker: {
            label: 'Did an insurance broker issue your letter of experience?',
          },
          loe_insurance: {
            label: 'Did an insurance company issue your letter of experience?',
          },
          loe_employer: {
            label: 'Did an employer issue your letter of experience?',
          },
          loe_upload: {
            label: 'Upload your letter of experience',
          },
        },
        job_policy_view: {
          add: {
            label: 'Add Job Policy',
          },
          page_title: {
            label: 'View Job Policy',
          },
          add_liability_slip: 'Add Liability Slip',
        },
        jobpolicy_edit: {
          company: {
            label: 'Insurance Company',
          },
        },
        policy_slip_view: {
          form: {
            delete: 'Delete Policy Slip',
          },
          policy_number: {
            label: 'Start Date',
          },
          letter_of_experience: {
            label: 'Policy Slip',
          },
        },
        vehicle_image: {
          save: {
            label: 'Save Image',
          },
        },
        vehicle_image_view: {
          page_title: {
            label: 'View Image',
          },
          form: {
            delete: 'Delete Image',
          },
        },
      },
      license: {
        view: {
          title: {
            label: 'Title',
          },
          company: {
            label: 'Company',
          },
        },
      },
      license_form: {
        start_date: {
          label: 'Start Date',
        },
        current: {
          label: 'Do you currently work here?',
        },
        end_date: {
          question: 'End Date',
        },
        loeHas: {
          label: 'Do you have a Letter Of Experience?',
        },
      },
      certification_form: {
        certification_file: {
          label: 'Attach your certification document',
        },
      },
    },
    profile: {
      experience: {
        location: 'Create property',
      },
      driver: {
        employment_history: {
          view: {
            add_insurance: 'Add Insurance Experience',
          },
        },
      },
    },
    company: {
      country: {
        label: 'Countries',
      },
      province: {
        label: 'Province',
      },
      address2: {
        question: 'Address Line 2',
      },
      name: {
        question: 'Address Line 1',
        title: 'Company Address',
        has: {
          question: 'Do you have a Company Name?',
        },
      },
      social: {
        title: 'Company Social Sites',
      },
      facebookUrl: {
        question: 'Enter your Facebook Url',
      },
      twitterUrl: {
        question: 'Enter your Twitter Url',
      },
      address: {
        title: 'Company Web Site',
      },
      company_policy: {
        form_view: {
          delete: 'Delete Company Policy',
        },
      },
      vehicletypes_edit: {
        loe_has: {
          label: 'Vehicle Status',
        },
      },
      vehicletypes_view: {
        vehicle_status: {
          label: 'Vehicle Status',
        },
        images: {
          add: 'Add Image',
          label: 'Images',
        },
      },
      vehicle_type: {
        vehicle_image_view: {
          page_title: {
            label: 'View Image',
          },
          form: {
            delete: 'Delete Image',
          },
        },
      },
      vehicle_image: {
        caption: {
          label: 'Photo Caption',
        },
      },
    },
    begin_password_reset_complete: {
      title: 'Check your email',
      description: 'Check your email for instructions to reset your account',
    },
    register: {
      label: {
        register_code: 'Register Code',
      },
    },
    services: {
      report_center: {
        invite_team_member: {
          email: 'E-mail address',
          account_number: 'DriverVerified Account Number',
          send: 'Send invitation',
        },
        invite_client: {
          title: 'Invite Client',
        },
        update_requirements: {
          title: 'Update Requirements',
        },
        add_to_do: {
          title: 'Add task',
        },
        view: {
          uploaded_date: 'Originally uploaded on {0}',
        },
        accountability_files: {
          cvord: {
            title: 'Accountability files',
          },
        },
      },
    },
    manage_account: {
      preferences: {
        field: {
          timezone: 'Time Zone',
        },
      },
    },
    units_and_measurements: {
      select: {
        time_zone: 'Select time zone',
        timezone: 'Select time zone...',
      },
    },
    timezone: {
      label: 'Time Zone',
      select: {
        timezone: 'Select time zone',
      },
    },
  },
  email: {
    reset_password: {
      salutation: 'Dear {0}',
    },
  },
  // Resource names
  resources: {
    drivers: {
      name: 'Driver |||| Drivers',
      fields: {
        profileState: 'Profile State',
        hasCanadaExperience: 'Canadian Experience',
        experienceRadius: 'Experience Radius',
        user: 'User',
      },
    },
    'driver-licenses': {
      name: 'License |||| Licenses',
      fields: {
        driver: 'Driver',
        licenseType: 'License Type',
        number: 'License Number',
        issuedDate: 'Issued Date',
        expiryDate: 'Expiry Date',
        issuingJurisdiction: 'Issuing Jurisdiction',
      },
    },
    'driver-certifications': {
      name: 'Certification |||| Certifications',
      fields: {
        driver: 'Driver',
        certificationType: 'Certification Type',
        issuedDate: 'Issued Date',
        expiryDate: 'Expiry Date',
        issuingOrganization: 'Issuing Organization',
      },
    },
    'driver-education': {
      name: 'Education |||| Education',
      fields: {
        driver: 'Driver',
        educationSchool: 'School',
        programTitle: 'Program Title',
        schoolNameOther: 'Custom School Name',
        completedDate: 'Completed Date',
        status: 'Status',
      },
    },
    'driver-employment': {
      name: 'Employment |||| Employment',
      fields: {
        driver: 'Driver',
        company: 'Company',
        title: 'Title',
        startDate: 'Start Date',
        endDate: 'End Date',
        current: 'Current',
        description: 'Description',
        address1: 'Address Line 1',
        address2: 'Address Line 2',
        city: 'City',
        regionCode: 'Province/State',
        countryCode: 'Country',
        postalCode: 'Postal Code',
        contactHas: 'Contact Available',
        contactName: 'Contact Name',
        contactTitle: 'Contact Title',
        contactPhone: 'Contact Phone',
        contactPhoneExt: 'Extension',
        contactEmail: 'Contact Email',
      },
    },
    'driver-vehicles': {
      name: 'Vehicle Experience |||| Vehicle Experience',
      fields: {
        driver: 'Driver',
        status: 'Status',
        jobVehicleYear: 'Year',
        jobVehicleMake: 'Make',
        jobVehicleModel: 'Model',
        jobVehicleUserStatus: 'User Status',
        lengthMonths: 'Duration (Months)',
        mode: 'Mode',
        typeOther: 'Other Vehicle Type',
        notes: 'Notes',
      },
    },
    equipment: {
      name: 'Vehicle |||| Vehicles',
      fields: {
        // Basic Information
        id: 'ID',
        name: 'Equipment Name',
        type: 'Equipment Type',
        status: 'Status',
        active: 'Active Equipment',
        mode: 'Operational Mode',

        // Identification
        vin: 'VIN',
        licensePlate: 'License Plate',
        unitNumber: 'Unit Number',
        color: 'Color',

        // Make/Model/Year
        manufacturedYear: 'Year Manufactured',
        make: 'Make',
        model: 'Model',
        engineModel: 'Engine Model',
        transmission: 'Transmission Type',
        fuelType: 'Fuel Type',

        // Weight & Capacity
        maxWeightCapacity: 'Max Weight Capacity (lbs)',
        tareWeight: 'Tare Weight (lbs)',
        grossVehicleWeight: 'Gross Vehicle Weight (lbs)',

        // Trailer Specific
        trailerAxleCount: 'Axle Count',
        trailerLength: 'Length (ft)',
        trailerType: 'Trailer Type',

        // Ownership & Financial
        ownershipType: 'Ownership Type',
        acquireDate: 'Acquisition Date',
        disposeDate: 'Disposal Date',
        purchasePrice: 'Purchase Price',
        currentValue: 'Current Value',
        leaseCost: 'Monthly Lease Cost',

        // Compliance & Registration
        cvor: 'CVOR Number',
        usDotNumber: 'US DOT Number',
        registrationExpiry: 'Registration Expiry',
        safetyExpiry: 'Safety Inspection Expiry',
        insuranceExpiry: 'Insurance Expiry',

        // Mileage & Service
        currentMileage: 'Current Mileage',
        currentEngineHours: 'Current Engine Hours',
        lastServiceMileage: 'Last Service Mileage',
        lastServiceDate: 'Last Service Date',
        nextServiceDue: 'Next Service Due',

        // Technology Features
        hasGPS: 'GPS Tracking',
        hasELD: 'Electronic Logging Device',
        hasDashCam: 'Dash Camera',

        // Additional
        notes: 'Notes',
      },
      sections: {
        basic_info: 'Basic Information',
        identification: 'Vehicle/Equipment Identification',
        specifications: 'Make, Model & Specifications',
        weight_capacity: 'Weight & Capacity',
        trailer_specs: 'Trailer Specifications',
        ownership_financial: 'Ownership & Financial Information',
        compliance: 'Compliance & Registration',
        mileage_service: 'Mileage & Service Tracking',
        technology: 'Technology Features',
        additional: 'Additional Information',
      },
      help: {
        name: 'Enter a descriptive name for this equipment',
        type: 'Select the type of equipment',
        vin: 'Vehicle Identification Number',
        licensePlate: 'License plate number',
        unitNumber: 'Internal fleet unit number',
        manufacturedYear: 'Year the equipment was manufactured',
        make: 'Manufacturer (e.g., Ford, Freightliner)',
        model: 'Model name/number',
        maxWeightCapacity: 'Maximum weight capacity in lbs',
        tareWeight: 'Empty weight in lbs',
        grossVehicleWeight: 'GVW in lbs',
        trailerAxleCount: 'Number of axles (for trailers)',
        trailerLength: 'Trailer length in feet',
        acquireDate: 'Date equipment was acquired',
        purchasePrice: 'Purchase price in dollars',
        currentValue: 'Current market value',
        leaseCost: 'Monthly lease cost',
        cvor: 'Commercial Vehicle Operator Registration number',
        usDotNumber: 'US Department of Transportation number',
        currentMileage: 'Current odometer reading',
        currentEngineHours: 'Current engine hours',
        lastServiceMileage: 'Mileage at last service',
        nextServiceDue: 'Date next service is due',
        hasGPS: 'Equipment has GPS tracking device',
        hasELD: 'Equipment has ELD installed',
        hasDashCam: 'Equipment has dash camera system',
        active: 'Whether this equipment is currently in service',
        notes: 'Additional notes or comments about this equipment',
      },
      status: {
        ACTIVE: 'Active',
        INACTIVE: 'Inactive',
        MAINTENANCE: 'Maintenance',
        RETIRED: 'Retired',
        DELETED: 'Deleted',
      },
      types: {
        TRUCK: 'Truck',
        TRAILER: 'Trailer',
        TRACTOR: 'Tractor',
        VAN: 'Van',
        CAR: 'Car',
        BUS: 'Bus',
        EQUIPMENT: 'Equipment',
        CRANE: 'Crane',
        FORKLIFT: 'Forklift',
        BULLDOZER: 'Bulldozer',
        EXCAVATOR: 'Excavator',
        CEMENT_TRUCK: 'Cement Truck',
        DUMP_TRUCK: 'Dump Truck',
        GARBAGE_TRUCK: 'Garbage Truck',
        TOW_TRUCK: 'Tow Truck',
        DELIVERY_VAN: 'Delivery Van',
        FLATBED: 'Flatbed',
        OTHER: 'Other',
      },
      ownership_types: {
        OWNED: 'Owned',
        LEASED: 'Leased',
        RENTED: 'Rented',
      },
      transmission_types: {
        MANUAL: 'Manual',
        AUTOMATIC: 'Automatic',
        CVT: 'CVT',
      },
      fuel_types: {
        DIESEL: 'Diesel',
        GAS: 'Gasoline',
        ELECTRIC: 'Electric',
        HYBRID: 'Hybrid',
        CNG: 'Compressed Natural Gas',
        LPG: 'Liquid Petroleum Gas',
      },
      trailer_types: {
        FLATBED: 'Flatbed',
        REEFER: 'Refrigerated',
        DRY_VAN: 'Dry Van',
        TANK: 'Tank',
        LOWBOY: 'Lowboy',
        STEP_DECK: 'Step Deck',
        CONESTOGA: 'Conestoga',
      },
      notifications: {
        created: 'Equipment created successfully',
        updated: 'Equipment updated successfully',
        deleted: 'Equipment deleted successfully',
      },
    },
    equipment_maintenance: {
      name: 'Maintenance Record |||| Maintenance Records',
      fields: {
        equipment: 'Equipment',
        serviceDate: 'Service Date',
        serviceType: 'Service Type',
        scheduledService: 'Scheduled Service',
        serviceMileage: 'Service Mileage',
        serviceEngineHours: 'Engine Hours',
        serviceProvider: 'Service Provider',
        providerPhone: 'Provider Phone',
        providerAddress: 'Provider Address',
        providerEmail: 'Provider Email',
        description: 'Service Description',
        notes: 'Additional Notes',
        laborHours: 'Labor Hours',
        partsCost: 'Parts Cost',
        laborCost: 'Labor Cost',
        otherCost: 'Other Costs',
        totalCost: 'Total Cost',
        invoiceNumber: 'Invoice Number',
        underWarranty: 'Under Warranty',
        warrantyClaimNumber: 'Warranty Claim Number',
        nextServiceDue: 'Next Service Due',
        nextServiceMileage: 'Next Service Mileage',
      },
      sections: {
        service_info: 'Service Information',
        mileage_hours: 'Mileage & Hours at Service',
        provider_info: 'Service Provider',
        service_details: 'Service Details',
        cost_breakdown: 'Cost Breakdown',
        invoice_warranty: 'Invoice & Warranty',
        next_service: 'Next Service Due',
      },
      service_types: {
        OIL_CHANGE: 'Oil Change',
        INSPECTION: 'Inspection',
        REPAIR: 'Repair',
        TIRE_SERVICE: 'Tire Service',
        BRAKE_SERVICE: 'Brake Service',
        ENGINE_SERVICE: 'Engine Service',
        TRANSMISSION_SERVICE: 'Transmission Service',
        ELECTRICAL: 'Electrical',
        BODYWORK: 'Bodywork',
        PREVENTIVE: 'Preventive Maintenance',
        EMERGENCY: 'Emergency Repair',
        OTHER: 'Other',
      },
      help: {
        scheduledService: 'Was this a scheduled maintenance?',
        serviceMileage: 'Odometer reading at time of service',
        serviceEngineHours: 'Engine hours at time of service',
        serviceProvider: 'Name of service provider or shop',
        description: 'Detailed description of work performed',
        notes: 'Any additional notes or observations',
        laborHours: 'Total labor hours',
        partsCost: 'Cost of parts and materials',
        laborCost: 'Cost of labor',
        otherCost: 'Other miscellaneous costs',
        totalCost: 'Total cost of service',
        underWarranty: 'Was this service covered under warranty?',
        warrantyClaimNumber: 'Warranty claim number if applicable',
        nextServiceMileage: 'Mileage when next service is due',
      },
      notifications: {
        created: 'Maintenance record created successfully',
        updated: 'Maintenance record updated successfully',
        deleted: 'Maintenance record deleted successfully',
        delete_error: 'Error deleting maintenance record',
      },
      create: {
        title: 'Add Maintenance Record',
      },
      edit: {
        title: 'Edit Maintenance Record',
      },
      delete: {
        title: 'Delete Maintenance Record',
        content:
          'Are you sure you want to delete this maintenance record? This action cannot be undone.',
      },
    },
    equipment_fuel: {
      name: 'Fuel Record |||| Fuel Records',
      fields: {
        equipment: 'Equipment',
        fuelDate: 'Fuel Date',
        fuelType: 'Fuel Type',
        quantity: 'Quantity',
        unitCost: 'Unit Cost',
        totalCost: 'Total Cost',
        mileage: 'Mileage',
        engineHours: 'Engine Hours',
        location: 'Location',
        vendor: 'Vendor',
      },
      sections: {
        fuel_info: 'Fuel Record Information',
        quantity_cost: 'Quantity & Cost',
        mileage_hours: 'Mileage & Engine Hours',
        location_vendor: 'Location & Vendor',
      },
      fuel_types: {
        DIESEL: 'Diesel',
        GAS: 'Gasoline',
        DEF: 'Diesel Exhaust Fluid',
        HYDRAULIC: 'Hydraulic Fluid',
        ENGINE_OIL: 'Engine Oil',
        OTHER: 'Other',
      },
      help: {
        quantity: 'Amount in gallons or liters',
        unitCost: 'Cost per gallon/liter',
        totalCost: 'Total amount paid',
        mileage: 'Odometer reading at fuel up',
        engineHours: 'Engine hours at fuel up',
        location: 'Fuel station location or address',
        vendor: 'Fuel station name or vendor',
      },
      notifications: {
        created: 'Fuel record created successfully',
        updated: 'Fuel record updated successfully',
        deleted: 'Fuel record deleted successfully',
        delete_error: 'Error deleting fuel record',
      },
      create: {
        title: 'Add Fuel Record',
      },
      edit: {
        title: 'Edit Fuel Record',
      },
      delete: {
        title: 'Delete Fuel Record',
        content: 'Are you sure you want to delete this fuel record? This action cannot be undone.',
      },
    },
    equipment_inspections: {
      name: 'Inspection |||| Inspections',
      fields: {
        equipment: 'Equipment',
        inspectionDate: 'Inspection Date',
        inspectionType: 'Inspection Type',
        inspector: 'Inspector',
        inspectionNumber: 'Inspection Number',
        passed: 'Passed',
        nextDueDate: 'Next Due Date',
      },
      inspection_types: {
        ANNUAL: 'Annual Inspection',
        CVOR: 'CVOR Inspection',
        DOT: 'DOT Inspection',
        PRE_TRIP: 'Pre-Trip Inspection',
        POST_TRIP: 'Post-Trip Inspection',
        SAFETY: 'Safety Inspection',
        EMISSION: 'Emission Test',
        OTHER: 'Other',
      },
      status: {
        passed: 'Passed',
        failed: 'Failed',
      },
      create: {
        title: 'Add Inspection Record',
      },
      edit: {
        title: 'Edit Inspection Record',
      },
    },
    equipment_assignments: {
      name: 'Equipment Assignment |||| Equipment Assignments',
      fields: {
        equipment: 'Equipment',
        driver: 'Driver',
        no_driver: 'No Driver Assigned',
        assigned_date: 'Assigned Date',
        returned_date: 'Returned Date',
        mileage_out: 'Mileage Out',
        mileage_in: 'Mileage In',
        condition: 'Equipment Condition',
        notes: 'Assignment Notes',
        days: 'days',
      },
      sections: {
        assignment_info: 'Assignment Information',
        mileage_info: 'Mileage Information',
        condition_notes: 'Condition & Notes',
      },
      status: {
        active: 'Active',
        returned: 'Returned',
      },
      help: {
        returned_date: 'Leave empty if equipment is still assigned',
        mileage_out: 'Odometer reading when equipment was assigned',
        mileage_in: 'Odometer reading when equipment was returned',
        condition: 'Describe the condition of the equipment',
        notes: 'Additional notes about this assignment',
      },
      notifications: {
        created: 'Equipment assignment created successfully',
        updated: 'Equipment assignment updated successfully',
        deleted: 'Equipment assignment deleted successfully',
      },
      create: {
        title: 'Add Assignment Record',
      },
      edit: {
        title: 'Edit Assignment Record',
      },
    },
    equipment_incidents: {
      name: 'Equipment Incident |||| Equipment Incidents',
      fields: {
        equipment: 'Equipment',
        incident_date: 'Incident Date',
        incident_type: 'Incident Type',
        driver: 'Driver Involved',
        no_driver: 'No Driver Involved',
        description: 'Incident Description',
        location: 'Location',
        repair_cost: 'Repair Cost',
        downtime_days: 'Downtime (Days)',
        insurance_claim: 'Insurance Claim Filed',
        claim_number: 'Claim Number',
        resolved: 'Incident Resolved',
        resolved_date: 'Resolved Date',
      },
      sections: {
        incident_info: 'Incident Information',
        incident_details: 'Incident Details',
        financial_impact: 'Financial Impact',
        insurance_info: 'Insurance Information',
        resolution_status: 'Resolution Status',
      },
      incident_types: {
        accident: 'Accident',
        breakdown: 'Breakdown',
        theft: 'Theft',
        damage: 'Damage',
        vandalism: 'Vandalism',
        maintenance_issue: 'Maintenance Issue',
        safety_issue: 'Safety Issue',
        collision: 'Collision',
        fire: 'Fire',
        flood: 'Flood',
        other: 'Other',
      },
      status: {
        open: 'Open',
        resolved: 'Resolved',
      },
      help: {
        description: 'Detailed description of what happened',
        location: 'Where the incident occurred',
        repair_cost: 'Estimated or actual repair cost',
        downtime_days: 'Number of days equipment was out of service',
        insurance_claim: 'Was an insurance claim filed for this incident?',
        claim_number: 'Insurance claim number if applicable',
        resolved: 'Has this incident been fully resolved?',
        resolved_date: 'Date the incident was resolved',
      },
      notifications: {
        created: 'Equipment incident created successfully',
        updated: 'Equipment incident updated successfully',
        deleted: 'Equipment incident deleted successfully',
      },
      create: {
        title: 'Add Incident Report',
      },
      edit: {
        title: 'Edit Incident Report',
      },
    },
  },
};

export default customEnglishMessages as TranslationMessages;
